/**
 * Client HTTP typé.
 *
 * Deux partis pris :
 *
 * - les erreurs du serveur arrivent au format RFC 7807 et sont converties en
 *   `ApiError` portant le `detail` **tel qu'écrit par le serveur**. Les
 *   messages métier sont rédigés en langage humain côté API (brief §17), donc
 *   les réécrire ici ne ferait que les dégrader ;
 * - le jeton est conservé dans `localStorage`. En V1 il n'y a pas de
 *   rafraîchissement silencieux : une expiration renvoie à l'écran de
 *   connexion, ce qui est acceptable pour un outil utilisé quelques fois par
 *   mois.
 */
import type {
  Availability,
  AvailabilityItem,
  AvailabilitySummary,
  Candidate,
  EventSlots,
  EventSummary,
  EventType,
  EventTypeUpdate,
  EventUpdate,
  Lock,
  MyAssignment,
  Person,
  PersonUpdate,
  Preference,
  Profile,
  Requirement,
  RequirementItem,
  Role,
  RoleCompatibility,
  RolePairPreference,
  RunDetail,
  RunRequest,
  RunSummary,
  Rule,
  RuleCreate,
  Skill,
  SlotUpdate,
  Team,
} from './types';

const TOKEN_KEY = 'planning.token';
const BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
    readonly fieldErrors: { field: string; message: string }[] = [],
  ) {
    super(detail);
    this.name = 'ApiError';
  }
}

export function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function writeToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(TOKEN_KEY);
    else localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* navigation privée : on continue sans persistance */
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

function buildUrl(path: string, query?: Query): string {
  if (!query) return `${BASE}${path}`;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  }
  const suffix = params.toString();
  return suffix ? `${BASE}${path}?${suffix}` : `${BASE}${path}`;
}

async function request<T>(
  method: string,
  path: string,
  options: { body?: unknown; query?: Query } = {},
): Promise<T> {
  const token = readToken();
  const response = await fetch(buildUrl(path, options.query), {
    method,
    headers: {
      ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const detail =
      (payload && typeof payload.detail === 'string' && payload.detail) ||
      `Erreur ${response.status}`;
    const fieldErrors = Array.isArray(payload?.errors) ? payload.errors : [];
    throw new ApiError(response.status, detail, fieldErrors);
  }
  return payload as T;
}

/**
 * Téléchargement d'un fichier produit par l'API.
 *
 * Un `<a href>` ne conviendrait pas : le jeton voyage dans un en-tête, pas
 * dans l'URL. On récupère donc les octets, puis on déclenche la sauvegarde
 * avec le nom de fichier choisi par le serveur — c'est lui qui connaît la
 * période demandée.
 */
async function download(path: string, query: Query, fallbackName: string): Promise<void> {
  const token = readToken();
  const response = await fetch(buildUrl(path, query), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const detail =
      (payload && typeof payload.detail === 'string' && payload.detail) ||
      `Erreur ${response.status}`;
    throw new ApiError(response.status, detail);
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const matched = /filename="?([^";]+)"?/i.exec(disposition);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = matched?.[1] ?? fallbackName;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const api = {
  login: (email: string, password: string) =>
    request<{ access_token: string }>('POST', '/auth/login', {
      body: { email, password },
    }),
  profile: () => request<Profile>('GET', '/auth/me'),

  teams: () => request<Team[]>('GET', '/teams'),
  roles: (teamId?: number) => request<Role[]>('GET', '/roles', { query: { team_id: teamId } }),

  persons: (teamId?: number) =>
    request<Person[]>('GET', '/persons', { query: { team_id: teamId } }),
  createPerson: (body: { first_name: string; last_name: string; team_ids: number[] }) =>
    request<Person>('POST', '/persons', { body }),
  person: (personId: number) => request<Person>('GET', `/persons/${personId}`),
  updatePerson: (personId: number, body: PersonUpdate) =>
    request<Person>('PATCH', `/persons/${personId}`, { body }),
  deletePerson: (personId: number) => request<void>('DELETE', `/persons/${personId}`),
  rolePairs: (personId: number) =>
    request<RolePairPreference[]>('GET', `/persons/${personId}/role-pairs`),
  replaceRolePairs: (personId: number, pairs: RolePairPreference[]) =>
    request<RolePairPreference[]>('PUT', `/persons/${personId}/role-pairs`, {
      body: { pairs },
    }),
  skills: (personId: number) => request<Skill[]>('GET', `/persons/${personId}/skills`),
  replaceSkills: (personId: number, skills: Skill[]) =>
    request<Skill[]>('PUT', `/persons/${personId}/skills`, { body: { skills } }),
  preferences: (personId: number) =>
    request<Preference[]>('GET', `/persons/${personId}/preferences`),
  replacePreferences: (personId: number, preferences: Preference[]) =>
    request<Preference[]>('PUT', `/persons/${personId}/preferences`, {
      body: { preferences },
    }),
  rules: (personId: number) => request<Rule[]>('GET', `/persons/${personId}/rules`),
  createRule: (personId: number, body: RuleCreate) =>
    request<Rule>('POST', `/persons/${personId}/rules`, { body }),
  deleteRule: (personId: number, ruleId: number) =>
    request<void>('DELETE', `/persons/${personId}/rules/${ruleId}`),

  roleCompatibility: () =>
    request<RoleCompatibility[]>('GET', '/roles/compatibility'),
  replaceRoleCompatibility: (pairs: RoleCompatibility[]) =>
    request<RoleCompatibility[]>('PUT', '/roles/compatibility', { body: { pairs } }),

  eventTypes: () => request<EventType[]>('GET', '/event-types'),
  createEventType: (body: { name: string; description?: string | null }) =>
    request<EventType>('POST', '/event-types', { body }),
  updateEventType: (typeId: number, body: EventTypeUpdate) =>
    request<EventType>('PATCH', `/event-types/${typeId}`, { body }),
  deleteEventType: (typeId: number) =>
    request<void>('DELETE', `/event-types/${typeId}`),
  eventTypeRequirements: (typeId: number) =>
    request<Requirement[]>('GET', `/event-types/${typeId}/requirements`),
  replaceEventTypeRequirements: (typeId: number, requirements: RequirementItem[]) =>
    request<Requirement[]>('PUT', `/event-types/${typeId}/requirements`, {
      body: { requirements },
    }),
  events: (from?: string, to?: string) =>
    request<EventSummary[]>('GET', '/events', { query: { date_from: from, date_to: to } }),
  createEvent: (body: {
    title: string;
    starts_at: string;
    event_type_id?: number | null;
  }) => request<EventSummary>('POST', '/events', { body }),
  event: (eventId: number) => request<EventSummary>('GET', `/events/${eventId}`),
  updateEvent: (eventId: number, body: EventUpdate) =>
    request<EventSummary>('PATCH', `/events/${eventId}`, { body }),
  deleteEvent: (eventId: number) => request<void>('DELETE', `/events/${eventId}`),
  replaceRequirements: (eventId: number, requirements: RequirementItem[]) =>
    request<Requirement[]>('PUT', `/events/${eventId}/requirements`, {
      body: { requirements },
    }),
  candidates: (eventId: number) =>
    request<Candidate[]>('GET', `/events/${eventId}/candidates`),

  availability: (personId?: number, eventId?: number) =>
    request<Availability[]>('GET', '/availability', {
      query: { person_id: personId, event_id: eventId },
    }),
  availabilitySummary: (from?: string, to?: string) =>
    request<AvailabilitySummary>('GET', '/availability/summary', {
      query: { date_from: from, date_to: to },
    }),
  replaceAvailability: (personId: number, items: AvailabilityItem[]) =>
    request<Availability[]>('PUT', `/availability/${personId}/bulk`, { body: { items } }),

  createRun: (body: RunRequest) => request<RunDetail>('POST', '/planning/runs', { body }),
  runs: (planStatus?: string) =>
    request<RunSummary[]>('GET', '/planning/runs', { query: { plan_status: planStatus } }),
  run: (runId: number) => request<RunDetail>('GET', `/planning/runs/${runId}`),
  regenerate: (runId: number) =>
    request<RunDetail>('POST', `/planning/runs/${runId}/regenerate`),
  publish: (runId: number) =>
    request<RunSummary>('POST', `/planning/runs/${runId}/publish`),

  locks: (eventId?: number) =>
    request<Lock[]>('GET', '/planning/locks', { query: { event_id: eventId } }),
  createLock: (body: { event_id: number; person_id: number; role_id: number }) =>
    request<Lock>('POST', '/planning/locks', { body }),
  deleteLock: (lockId: number) => request<void>('DELETE', `/planning/locks/${lockId}`),

  duplicateAsDraft: (runId: number) =>
    request<RunDetail>('POST', `/planning/runs/${runId}/duplicate-as-draft`),

  // Édition des affectations : les candidats renvoyés tiennent déjà compte
  // des contraintes, y compris de celles créées par l'édition en cours.
  periodSlots: (runId: number) =>
    request<EventSlots[]>('GET', `/planning/runs/${runId}/slots`),
  eventSlots: (runId: number, eventId: number) =>
    request<EventSlots>('GET', `/planning/runs/${runId}/events/${eventId}/slots`),
  updateSlot: (runId: number, eventId: number, roleId: number, body: SlotUpdate) =>
    request<EventSlots>(
      'PUT',
      `/planning/runs/${runId}/events/${eventId}/roles/${roleId}`,
      { body },
    ),

  exportXlsx: (from: string, to: string) =>
    download('/planning/export.xlsx', { date_from: from, date_to: to }, 'planning.xlsx'),
  exportPdf: (from: string, to: string) =>
    download('/planning/export.pdf', { date_from: from, date_to: to }, 'planning.pdf'),

  myAssignments: () => request<MyAssignment[]>('GET', '/me/assignments'),
};
